import { classNames } from 'primereact/utils';
import { Button } from '../atoms';
import { QuoteOutlineIcon, RotateClockwiseIcon, RotateAnticlockwiseIcon } from '../atoms/icons';
import { FC, ReactNode } from 'react';

interface ZoomAndNavigatePdfPanelProps {
  zoomIn?: () => void;
  zoomOut?: () => void;
  rotateClockwise?: () => void;
  rotateAnticlockwise?: () => void;
  pageUp?: () => void;
  pageDown?: () => void;
  onAddToChronology?: () => void;
  /** Highlights the chronology button when the page has chronology entries. */
  addToChronologyActive?: boolean;
  /**
   * Replaces the built-in chronology button (rendered in the same 32×32 box). `onAddToChronology` is ignored when set.
   */
  chronologySlot?: ReactNode;
  zoomInDisabled?: boolean;
  zoomOutDisabled?: boolean;
  rotateDisabled?: boolean;
  /** Optional slot below the chronology button, for app-specific controls. */
  bottomSlot?: ReactNode;
  /** Optional slot below `bottomSlot`, rendered in its own rail group. */
  footerSlot?: ReactNode;
}

/**
 * Vertical PDF control rail: zoom, rotate and page navigation. `pt={{ label: '!hidden' }}` hides
 * PrimeReact's empty label, which would push the icon left.
 */
export const ZoomAndNavigatePdfPanel: FC<ZoomAndNavigatePdfPanelProps> = ({
  zoomIn,
  zoomOut,
  rotateClockwise,
  rotateAnticlockwise,
  pageUp,
  pageDown,
  onAddToChronology,
  addToChronologyActive,
  chronologySlot,
  zoomInDisabled,
  zoomOutDisabled,
  rotateDisabled,
  bottomSlot,
  footerSlot,
}) => {
  if (
    !zoomIn &&
    !zoomOut &&
    !rotateClockwise &&
    !rotateAnticlockwise &&
    !pageUp &&
    !pageDown &&
    !onAddToChronology &&
    !chronologySlot &&
    !bottomSlot &&
    !footerSlot
  ) {
    return null;
  }

  const zincColor = 'var(--zinc-950, #09090B)';
  const disabledColor = '#A0A0A0';

  const buttonBase = '!h-[32px] !w-[32px] !p-0 justify-center hover:!bg-gray-50';
  const hideLabel = { label: { className: '!hidden' } };
  // Tooltips on the left, centred on the button, so they aren't clipped by the viewer edge.
  const tooltipOptions = {
    position: 'left' as const,
    showDelay: 200,
    at: 'left-8 top',
    my: 'right top',
    // 32px tooltip height for the rail (`[role='tooltip'].zoom-rail-tooltip`).
    className: 'zoom-rail-tooltip',
  };

  const divider = <div className="bg-fletchers-gray-110 h-px" />;

  const railClassName = classNames(
    'flex flex-col',
    'bg-fletchers-white rounded-[4px]',
    'border-fletchers-gray-110 border',
    'overflow-hidden',
    'shadow-[0px_0px_4px_0px_#00000026]',
  );

  const hasMainRail = !!(zoomIn || zoomOut || rotateClockwise || rotateAnticlockwise || pageUp || pageDown);

  return (
    <div className={classNames('absolute top-1/2 right-6 z-10 -translate-y-1/2 transform', 'flex flex-col gap-2')}>
      {hasMainRail && (
        <div className={railClassName}>
          {zoomIn && (
            <Button
              text
              icon={<i className="pi pi-search-plus text-[0.875rem]" />}
              aria-label="Zoom in"
              tooltip="Zoom in"
              tooltipOptions={tooltipOptions}
              className={buttonBase}
              pt={hideLabel}
              style={{ color: zoomInDisabled ? disabledColor : zincColor }}
              onClick={zoomIn}
              disabled={zoomInDisabled}
            />
          )}
          {zoomOut && (
            <Button
              text
              icon={<i className="pi pi-search-minus text-[0.875rem]" />}
              aria-label="Zoom out"
              tooltip="Zoom out"
              tooltipOptions={tooltipOptions}
              className={buttonBase}
              pt={hideLabel}
              style={{ color: zoomOutDisabled ? disabledColor : zincColor }}
              onClick={zoomOut}
              disabled={zoomOutDisabled}
            />
          )}

          {(rotateClockwise || rotateAnticlockwise) && divider}

          {rotateAnticlockwise && (
            <Button
              text
              icon={<RotateAnticlockwiseIcon />}
              aria-label="Rotate page anticlockwise"
              tooltip="Rotate anticlockwise"
              tooltipOptions={tooltipOptions}
              className={buttonBase}
              pt={hideLabel}
              style={{ color: rotateDisabled ? disabledColor : zincColor }}
              onClick={rotateAnticlockwise}
              disabled={rotateDisabled}
            />
          )}
          {rotateClockwise && (
            <Button
              text
              icon={<RotateClockwiseIcon />}
              aria-label="Rotate page clockwise"
              tooltip="Rotate clockwise"
              tooltipOptions={tooltipOptions}
              className={buttonBase}
              pt={hideLabel}
              style={{ color: rotateDisabled ? disabledColor : zincColor }}
              onClick={rotateClockwise}
              disabled={rotateDisabled}
            />
          )}

          {(pageUp || pageDown) && divider}

          {pageUp && (
            <Button
              text
              icon={<i className="pi pi-arrow-up text-[0.875rem]" />}
              aria-label="Page up"
              tooltip="Page up"
              tooltipOptions={tooltipOptions}
              className={buttonBase}
              pt={hideLabel}
              style={{ color: zincColor }}
              onClick={pageUp}
            />
          )}
          {pageDown && (
            <Button
              text
              icon={<i className="pi pi-arrow-down text-[0.875rem]" />}
              aria-label="Page down"
              tooltip="Page down"
              tooltipOptions={tooltipOptions}
              className={buttonBase}
              pt={hideLabel}
              style={{ color: zincColor }}
              onClick={pageDown}
            />
          )}
        </div>
      )}
      {chronologySlot && <div className={railClassName}>{chronologySlot}</div>}
      {!chronologySlot && onAddToChronology && (
        <div className={railClassName}>
          <Button
            text
            icon={
              <QuoteOutlineIcon width={17} className={addToChronologyActive ? 'text-fletchers-teal-500' : undefined} />
            }
            aria-label="Add to Chronology"
            tooltip="Add to Chronology"
            tooltipOptions={tooltipOptions}
            className={buttonBase}
            pt={hideLabel}
            style={{ color: zincColor }}
            onClick={onAddToChronology}
          />
        </div>
      )}
      {bottomSlot && <div className={railClassName}>{bottomSlot}</div>}
      {footerSlot && <div className={railClassName}>{footerSlot}</div>}
    </div>
  );
};
